/**
 * Theme toggle composable
 *
 * Manages light/dark theme switching with localStorage persistence
 * and system preference fallback (prefers-color-scheme)
 */

import { computed } from 'vue'
import { useTheme as useVuetifyTheme } from 'vuetify'

const THEME_KEY = 'collector-theme'

export function useThemeToggle() {
  const theme = useVuetifyTheme()

  /**
   * Initialize theme from localStorage or system preference
   * Priority: 1. localStorage, 2. system preference, 3. default light
   */
  const initTheme = (): void => {
    const saved = localStorage.getItem(THEME_KEY)
    if (saved === 'light' || saved === 'dark') {
      theme.global.name.value = saved
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      theme.global.name.value = 'dark'
    } else {
      theme.global.name.value = 'light'
    }
  }

  /**
   * Toggle between light and dark themes
   * Persists choice to localStorage
   */
  const toggleTheme = (): void => {
    const newTheme = theme.global.current.value.dark ? 'light' : 'dark'
    theme.global.name.value = newTheme
    localStorage.setItem(THEME_KEY, newTheme)
  }

  /**
   * Set a specific theme
   * @param themeName - 'light' or 'dark'
   */
  const setTheme = (themeName: 'light' | 'dark'): void => {
    theme.global.name.value = themeName
    localStorage.setItem(THEME_KEY, themeName)
  }

  /**
   * Reactive computed property for current dark mode state
   */
  const isDark = computed(() => theme.global.current.value.dark)

  /**
   * Current theme name
   */
  const currentTheme = computed(() => theme.global.name.value)

  return {
    initTheme,
    toggleTheme,
    setTheme,
    isDark,
    currentTheme,
  }
}
